import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import React from 'react';

const TileInner = ({ company, date, description, genre, isPast, location, name, price, subVenueNames, thumbnail, type, url, venue, isActive = true }) => {
    // Visible link in the heading which can be tab focused
    const renderHeadingLink = () => {
        return (
            <Link className="card-link" to={url} tabIndex={isActive ? undefined : -1}>{name}</Link>
        );
    };

    // invisible link which must be a direct child of .tile element to cover the whole card
    // cannot be tab focused, we use the heading link for that instead
    const renderClickableLink = () => {
        return (
            <Link className="card-link-clickable" to={url} tabIndex={-1} aria-hidden="true">{name}</Link>
        );
    };

    const renderPageThumbnail = () => {
        const thumbnailImage = thumbnail.landscape || thumbnail.square;

        return (
            <div className="tile-thumbnail__page">
                <img className="image" src={thumbnailImage} alt={name} />
            </div>
        );
    };

    const getLocation = () => {
        if (venue) {
            return (<p className="location">{venue}</p>);
        } else if (location) {
            return (<p className="location">{location}</p>);
        }
    };

    const renderThumbnail = () => {
        const desktopImage = thumbnail.landscape;
        const mobileImage = thumbnail.square || thumbnail.landscape;

        return (
            <div className="tile-thumbnail">
                <img className="image desktop" src={desktopImage} alt={name} />
                <img className="image mobile" src={mobileImage} alt={name} />
            </div>
        );
    };

    const renderDate = () => {
        if (isPast) {
            return <p className="date past">Past Event</p>;
        }

        if (date) {
            return <time className="date">{date}</time>;
        }
    };

    const renderPrice = () => {
        if (isPast) {
            return <p className="price">No longer available</p>;
        }

        return price && <p className="price">{price}</p>;
    };

    const renderVenueInformation = () => (
        <div className="information">
            <p className="type">Venue</p>
            {name && <h3 className="h4 title">{renderHeadingLink()}</h3>}
            <div className="sub-information">
                {subVenueNames && <p className="sub-venues">{subVenueNames}</p>}
            </div>
            <div className="further">
                {getLocation()}
            </div>
        </div>
    );

    const renderEventInformation = () => (
        <div className="information">
            {company && <span className="h5 presenter">{company}</span>}
            {name && <h3 className="h4 title">{renderHeadingLink()}</h3>}
            <div className="sub-information">
                {renderDate()}
            </div>
            <div className="further">
                {renderPrice()}
                {getLocation()}
            </div>
        </div>
    );

    const renderNewsInformation = () => (
        <div className="information information-news">
            <p className="type type-news">News</p>
            {name && <h3 className="h4 title">{renderHeadingLink()}</h3>}
            {/* TODO: Implement date with publish time feature */}
            {/* <div className="sub-information">
                {renderDate()}
            </div> */}
            <div className="further">
                {description && <p className="description">{description}</p>}
            </div>
        </div>
    );

    const renderInformation = () => {
        if (type === 'venue') {
            return renderVenueInformation();
        }

        if (type === 'event') {
            return renderEventInformation();
        }

        if (type === 'news') {
            return renderNewsInformation();
        }

        // Show tile
        return (
            <div className="information">
                {company && <span className="h5 presenter">{company}</span>}
                {name && <h3 className="h4 title">{renderHeadingLink()}</h3>}
                <div className="sub-information">
                    {renderDate()}
                    {getLocation()}
                </div>
                <div className="further">
                    {renderPrice()}
                    {genre && <p className="genre">{genre}</p>}
                </div>
            </div>
        );
    };

    if (type === 'page') {
        return (
            <React.Fragment>
                {renderClickableLink()}
                <div className="title-page">
                    <h3 className="title">{renderHeadingLink()}</h3>
                </div>
                {renderPageThumbnail()}
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {/* invisible link which must be a direct child of .tile element to cover the whole card */}
            {renderClickableLink()}
            {renderInformation()}
            {renderThumbnail()}
        </React.Fragment>
    );
};

TileInner.propTypes = {
    company:  PropTypes.string,
    date:  PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.string
    ]),
    description: PropTypes.string,
    genre:  PropTypes.string,
    isPast: PropTypes.bool,
    location:  PropTypes.string,
    name:  PropTypes.string.isRequired,
    price:  PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.string,
    ]),
    shortDescription: PropTypes.string,
    subVenueNames: PropTypes.string,
    thumbnail:  PropTypes.object.isRequired,
    type:  PropTypes.string.isRequired,
    url:  PropTypes.string.isRequired,
    venue:  PropTypes.string,
    // whether the tile is currently in view
    isActive: PropTypes.bool,
};

export default TileInner;
